import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { AllowedAccess as LibraryAllowedAccess } from "react-permission-role";
import { useSelector } from "react-redux";

/**
 * `AllowedAccess` with a dependency the library can compare.
 *
 * react-permission-role@2 runs its check inside `useEffect(..., [roles,
 * permissions])` and sets two pieces of state there. Those props are arrays,
 * and every call site writes one as a JSX literal — a new array on each render.
 * So the effect saw a changed dependency every time, set state, caused the next
 * render, and React stopped it at fifty passes with "Maximum update depth
 * exceeded". Every guarded block on the screen did this, on every render.
 *
 * The lists themselves never change: they are fixed sets of permission names.
 * Memoising them by content — not by identity — gives the effect something
 * stable to compare, and the check runs once. Call sites keep writing the
 * literal they always wrote; the fix lives here rather than in each of them.
 *
 * The user's own roles and permissions can change while a page is open: a
 * renewal names them as the server holds them now (authSlice's grantsRenewed,
 * VAPT Portal 2). The library reads them from localStorage at each check, and
 * checks only when its lists change; new lists when the user's change make it
 * check again, the same lists while they do not.
 */
export const AllowedAccess = ({ roles, permissions, ...rest }) => {
  const rolesKey = Array.isArray(roles) ? roles.join("|") : "";
  const permissionsKey = Array.isArray(permissions)
    ? permissions.join("|")
    : "";
  // A guard of every page: a state without the lists (a store set up for a
  // test, say) reads as none rather than breaking the page it guards.
  const grantsKey = useSelector(
    (state) =>
      `${(state.auth?.roles ?? []).join("|")}#${(
        state.auth?.permissions ?? []
      ).join("|")}`
  );

  // A copy: when only the user's roles or permissions change, the call site
  // does not render, and its own array would reach the library unchanged.
  const stableRoles = useMemo(
    () => (Array.isArray(roles) ? [...roles] : roles),
    [rolesKey, grantsKey]
  );
  const stablePermissions = useMemo(
    () => (Array.isArray(permissions) ? [...permissions] : permissions),
    [permissionsKey, grantsKey]
  );

  return (
    <LibraryAllowedAccess
      roles={stableRoles}
      permissions={stablePermissions}
      {...rest}
    />
  );
};

AllowedAccess.propTypes = {
  roles: PropTypes.arrayOf(PropTypes.string),
  permissions: PropTypes.arrayOf(PropTypes.string),
};

export default AllowedAccess;
